import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArchive,
  faBox,
  faCog,
  faCopy,
  faPencil,
  faTrashAlt,
} from '@fortawesome/pro-light-svg-icons'

import { digObject } from '@domain-group/javascript-utils'

import {
  Box, Grid, IconWithMenu, Link, LoadingBubbles, StatusBadge, Text,
} from '@domain-group/suit-theme'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { LayoutTemplateModel } from '@models/layoutTemplate'

import PagePreview from '@components/PagePreview'
import PermissionIcon from '@components/TemplatePageGridTile/PermissionIcon'

import useLayoutTemplate from '@hooks/useLayoutTemplate'

const confirmDuplicate = (duplicateFn: () => void) => {
  const toggleLayoutTemplateVisibilityPayload: HandleCallbackActionParams = {
    callbacks: {
      // @ts-expect-error TS2322
      action: duplicateFn,
    },
    toastText: 'Layout Duplicated',
  }

  swal
    .fire({
      title: 'Duplicate Layout Template?',
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, duplicate it.',
      confirmButtonColor: '#DD6B55',
    })
    .then(({ value }) => {
      if (value){
        handleCallbackAction(toggleLayoutTemplateVisibilityPayload)
      }
    })
}

const confirmArchive = (toggleFn: () => void, hidden: boolean) => {
  const toastText = hidden ? 'Layout Unarchived' : 'Layout Archived'

  const toggleLayoutTemplateVisibilityPayload: HandleCallbackActionParams = {
    callbacks: {
      // @ts-expect-error TS2322
      action: toggleFn,
    },
    toastText,
  }

  swal
    .fire({
      title: `${hidden ? 'Unarchive' : 'Archive'} Layout Template?`,
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, ${hidden ? 'unarchive' : 'archive'} it.`,
      confirmButtonColor: '#DD6B55',
    })
    .then(({ value }) => {
      if (value){
        handleCallbackAction(toggleLayoutTemplateVisibilityPayload)
      }
    })
}

interface LayoutTemplateGridTileProps {
  layoutTemplate: LayoutTemplateModel,
}

const LayoutTemplateGridTile = (props: LayoutTemplateGridTileProps) => {
  const { layoutTemplate } = props

  const layoutTemplatePayload = useLayoutTemplate(layoutTemplate)
  const {
    callbacks: { assignLayoutTemplateProducts, duplicateLayoutTemplate: duplicateFn, toggleLayoutTemplateVisibility: toggleFn },
    compatibleProductsCount,
    createdByProvider,
    layoutTemplate: {
      hidden,
      // @ts-expect-error TS2339
      previews,
      title,
    },
    loading,
    provider,
  } = layoutTemplatePayload

  const previewImage = digObject(previews, 'large', '')
  const providerTitle = digObject(provider, 'title', '')

  return (
    <Box flexDirection="column">
      <Box
        alignItems="center"
        backgroundColor="whiteGrey"
        border="1px solid"
        borderColor="lineColor"
        borderRadius={5}
        borderBottomLeftRadius={0}
        borderBottomRightRadius={0}
        height={190}
        justifyContent="center"
        position="relative"
      >

        <StatusBadge
          boxProps={{
            left: 'medium',
            position: 'absolute',
            top: 'medium',
          }}
          color={hidden ? 'red' : 'lightGrey'}
          // @ts-expect-error TS2322
          text={(
            <Box as="span">
              <FontAwesomeIcon
                icon={hidden ? faTrashAlt : faPencil}
                style={{ marginRight: 4 }}
              />
              {hidden ? 'Archived' : 'Draft'}
            </Box>
          )}
        />

        {loading && (
          <Box
            alignItems="center"
            backgroundColor="rgba(255, 255, 255, 0.7)"
            bottom="0"
            display="flex"
            justifyContent="center"
            left="0"
            position="absolute"
            right="0"
            top="0"
          >
            <LoadingBubbles color="blue" />
          </Box>
        )}

        <PagePreview
          holderHeight={190}
          holderPadding={8}
          holderWidth={240}
          imageHeight={digObject(layoutTemplate, 'dimensions.pixels.height')}
          imageUrl={previewImage}
          imageWidth={digObject(layoutTemplate, 'dimensions.pixels.width')}
        />
      </Box>

      <Box
        border="1px solid"
        borderColor="lineColor"
        borderTop={0}
        borderBottomLeftRadius={5}
        borderBottomRightRadius={5}
        flexDirection="column"
      >
        <Box alignItems="center" borderBottom="1px solid" borderColor="lineColor" padding="large">
          <Box flexDirection="column" width="calc(100% - 16px)">
            <Link href="#/" textProps={{ color: 'bodyFontColor', fontSize: 'small', variant: 'ellipsis' }}>
              {title}
            </Link>

            <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="small" variant="ellipsis">
              Template #{layoutTemplate.id} {createdByProvider && providerTitle}
            </Text>
          </Box>

          <IconWithMenu
            boxProps={{ marginLeft: 'medium' }}
            icon={(
              <Link greyLink>
                <FontAwesomeIcon icon={faCog} />
              </Link>
            )}
          >
            {/*
             // @ts-expect-error TS2741 */}
            <IconWithMenu.Menu closeOnClick listStyle={{ right: '-22px' }}>
              <Link
                key="duplicate"
                onClick={() => confirmDuplicate(duplicateFn)}
              >
                <FontAwesomeIcon icon={faCopy} /> Duplicate
              </Link>

              <Link
                key="archive"
                onClick={() => confirmArchive(toggleFn, hidden)}
              >
                <FontAwesomeIcon icon={faArchive} /> {hidden ? 'Unarchive' : 'Archive'}
              </Link>

            </IconWithMenu.Menu>
          </IconWithMenu>
        </Box>

        {/*
         // @ts-expect-error TS2322 */}
        <Grid gridAutoRows="1fr" gridTemplateColumns="repeat(4, minmax(25%, 1fr))">
          <PermissionIcon
            callbacks={{ onClick: () => assignLayoutTemplateProducts('publicationProductIds') }}
            icon={<FontAwesomeIcon icon={faBox} />}
            label="Products"
            value={compatibleProductsCount}
          />
        </Grid>
      </Box>
    </Box>
  )
}

export default LayoutTemplateGridTile
